/* eslint-disable max-len */
/* eslint-disable indent */
/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { getClientes, getEdificios } from "../../utils";
import { calcularPorcentajeComisionEdificio, resolverDesgloseComision } from "../../utils/comisionEdificiosUtil";
import { getLocale } from "../../utils/getLocale";
import "./stylesListadoFacturas.scss";

export const ListadoFacturas = ({
  facturas,
  pantallasEdificio,
  comisionEdificio_1_porcentaje,
  comisionEdificio_1_fechainicio,
  comisionEdificio_1_fechafin,
  comisionEdificio_2_porcentaje,
  comisionEdificio_2_fechainicio,
  comisionEdificio_2_fechafin,
  comisionEdificio_3_porcentaje,
  comisionEdificio_3_fechainicio,
  comisionEdificio_3_fechafin,
  edificioId = null,
  pais,
  labelCobrada = "COBRADA",
  onTotalesCalculados = null,
  mostrarFooter = true,
  onDetalle = null,
  snapMap = null,
}) => {
  const [totales, setTotales] = useState({
    facturado: 0,
    comision: 0,
    comisionCobrada: 0,
    totalImporte: 0,
    totalComisionIntermediario: 0,
  });
  const [clientes, setClientes] = useState([]);
  const [edificios, setEdificios] = useState([]);

  const moneda = useCallback((p) => ({ Chile: "$ ", Perú: "S/ ", Uruguay: "$ " })[p] || "$ ", []);
  const decimales = useCallback((p) => ({ Chile: 0, Perú: 2, Uruguay: 0 })[p] || 0, []);

  // Objeto edificio para usar la función centralizada calcularPorcentajeComisionEdificio
  const edificioComision = useMemo(
    () => ({
      comision_1_porcentaje: comisionEdificio_1_porcentaje,
      comision_1_fechainicio: comisionEdificio_1_fechainicio,
      comision_1_fechafin: comisionEdificio_1_fechafin,
      comision_2_porcentaje: comisionEdificio_2_porcentaje,
      comision_2_fechainicio: comisionEdificio_2_fechainicio,
      comision_2_fechafin: comisionEdificio_2_fechafin,
      comision_3_porcentaje: comisionEdificio_3_porcentaje,
      comision_3_fechainicio: comisionEdificio_3_fechainicio,
      comision_3_fechafin: comisionEdificio_3_fechafin,
    }),
    [
      comisionEdificio_1_porcentaje,
      comisionEdificio_1_fechainicio,
      comisionEdificio_1_fechafin,
      comisionEdificio_2_porcentaje,
      comisionEdificio_2_fechainicio,
      comisionEdificio_2_fechafin,
      comisionEdificio_3_porcentaje,
      comisionEdificio_3_fechainicio,
      comisionEdificio_3_fechafin,
    ]
  );

  useEffect(() => {
    const fetch = async () => {
      try {
        const [c, e] = await Promise.all([getClientes({ pais }), getEdificios({ pais })]);
        setClientes(c || []);
        setEdificios(e || []);
      } catch {
        toast.error("Error al cargar datos");
      }
    };
    fetch();
  }, [pais]);

  useEffect(() => {
    if (facturas.length === 0 || clientes.length === 0 || edificios.length === 0) {
      setTotales({
        facturado: 0,
        comision: 0,
        comisionCobrada: 0,
        totalImporte: 0,
        totalComisionIntermediario: 0,
      });
      return;
    }

    let totalImporteBruto = 0; // Bruto proporcional
    let totalFacturadoNeto = 0; // Neto proporcional (después de intermediario)
    let totalComisionEdificio = 0;
    let totalComisionCobrada = 0;
    let totalComisionIntermediarioValor = 0; // Para cálculo interno del % promedio

    facturas.forEach((f) => {
      // Cálculo de pantallas ajustadas del edificio en esta factura (función centralizada)
      // Numerador Y denominador de la MISMA foto (Fase 2, resolverDesgloseComision): idéntico a lo que
      // usa la comisión, nunca numerador vivo ÷ denominador congelado (invariante de fuente única, 7.16).
      // Sin edificio específico (agregado) o BTL: numerador = total físico `pantallasEdificio` (default)
      // y denominador de la foto. Display puro (10.48): no toca neto ni snapshot.
      let pantallasAjustadas = pantallasEdificio;
      let denomComision = resolverDesgloseComision(f, null, snapMap).denominador;
      if (edificioId && !f.isBTL) {
        const edificioActual = edificios.find((e) => e.id === edificioId);
        if (edificioActual) {
          const desg = resolverDesgloseComision(f, edificioActual, snapMap);
          pantallasAjustadas = desg.numerador;
          denomComision = desg.denominador;
        }
      }
      const participacion = denomComision > 0 ? pantallasAjustadas / denomComision : 0;

      // Bruto proporcional
      const importeBrutoProp = f.isBTL ? f.importe : participacion * f.importe;
      totalImporteBruto += importeBrutoProp;

      // Neto proporcional (ya descontada comisión intermediario correcta)
      const importeNetoProp = f.importeNeto || importeBrutoProp;
      totalFacturadoNeto += importeNetoProp;

      // Comisión intermediario valor (para cálculo interno)
      totalComisionIntermediarioValor += importeBrutoProp - importeNetoProp;

      // % comisión edificio. BTL: usa building.comision (f.comision) sobre el importe del edificio, NO el
      // % de la tabla (regla BTL — ver ficha calculador-comision-edificio). PAUTA: % de la tabla por fecha
      // (intacto). Para BTL importeNetoProp ya == f.importe (neto = bruto), así que el producto da
      // f.importe × f.comision / 100.
      const porcComisionEdificio = f.isBTL
        ? f.comision || 0
        : calcularPorcentajeComisionEdificio(edificioComision, f.facturafecha);

      // Comisión edificio
      const comisionEdificio = importeNetoProp * (porcComisionEdificio / 100);
      totalComisionEdificio += comisionEdificio;

      // Comisión recaudada: cobro real (facturafechacobro ya overlaid) O regla de corte pre-2026
      // (mismo criterio que getSaldoEdificio.js:268) → el total coincide con las filas verdes "Recaudado".
      if ((f.facturafecha && f.facturafecha < "2026-01-01") || f.facturafechacobro) {
        totalComisionCobrada += comisionEdificio;
      }
    });

    setTotales({
      totalImporte: totalImporteBruto,
      facturado: totalFacturadoNeto,
      comision: totalComisionEdificio,
      comisionCobrada: totalComisionCobrada,
      totalComisionIntermediario: totalComisionIntermediarioValor,
    });
    onTotalesCalculados?.({
      bruto: totalImporteBruto,
      neto: totalFacturadoNeto,
      comisionIntermediario: totalComisionIntermediarioValor,
      generada: totalComisionEdificio,
      recaudada: totalComisionCobrada,
      pendiente: totalComisionEdificio - totalComisionCobrada,
    });
  }, [facturas, pantallasEdificio, edificios, edificioId, edificioComision, onTotalesCalculados, snapMap]);

  const facturasOrdenadas = useMemo(
    () => [...facturas].sort((a, b) => new Date(a.facturafecha) - new Date(b.facturafecha)),
    [facturas]
  );

  if (clientes.length === 0 || edificios.length === 0) return <p>Cargando datos...</p>;

  return (
    <div className="table-responsive">
      <table className="table table-fixed table-striped table-bordered table-hover">
        <colgroup>
          <col className="c-tipo" />
          <col className="c-numero" />
          <col className="c-fecha" />
          <col className="c-cliente" />
          <col className="c-pantallas" />
          <col className="c-share" />
          {onDetalle && <col className="c-detalle" />}
          <col className="c-bruto" />
          <col className="c-intermediario" />
          <col className="c-comision-int" />
          <col className="c-neto" />
          <col className="c-compct" />
          <col className="c-comval" />
          <col className="c-cobrada" />
        </colgroup>
        <thead className="text-center align-middle">
          <tr>
            <th className="tipo">TIPO</th>
            <th className="numero-factura">NÚMERO FACTURA</th>
            <th className="fecha-factura">FECHA FACTURA</th>
            <th className="cliente">CLIENTE</th>
            <th className="pantallas-participantes">PANTALLAS TOTALES</th>
            <th className="pantallas-involucradas">SHARE EDIFICIO</th>
            {onDetalle && <th className="detalle">DETALLE</th>}
            <th className="importe-bruto">IMPORTE BRUTO</th>
            <th className="intermediario">INTERMEDIARIO</th>
            <th className="comision-intermediario">COMISIÓN AGENCIA</th>
            <th className="importe-neto">IMPORTE NETO</th>
            <th className="comision-edificio" colSpan="2">
              COMISIÓN EDIFICIO
            </th>
            <th className="cobrada">{labelCobrada}</th>
          </tr>
        </thead>
        <tbody>
          {facturasOrdenadas.map((factura) => {
            const {
              cliente,
              facturafecha,
              facturafechacobro,
              facturanumero,
              id,
              importe,
              isBTL,
              plazocobro,
              intermediario = "DIRECTO",
              comision: comisionIntermediarioPorcentaje = 0,
              importeNeto = 0,
              __rawSale = null,
            } = factura;

            // Numerador Y denominador de la MISMA foto (Fase 2, resolverDesgloseComision): SHARE, BRUTO y
            // la columna PANTALLAS TOTALES miran la misma foto que la comisión. Sin edificio específico
            // (agregado) o BTL: numerador = total físico `pantallasEdificio` (default) + denominador de la foto.
            let pantallasEdificioAjustadas = pantallasEdificio;
            let denomComision = resolverDesgloseComision(factura, null, snapMap).denominador;
            if (edificioId && !isBTL) {
              const edificioActual = edificios.find((e) => e.id === edificioId);
              if (edificioActual) {
                const desg = resolverDesgloseComision(factura, edificioActual, snapMap);
                pantallasEdificioAjustadas = desg.numerador;
                denomComision = desg.denominador;
              }
            }

            // % comisión edificio. BTL: usa building.comision (viene en la fila como comisión "de
            // intermediario"), NO el % de la tabla — regla BTL, ver ficha calculador-comision-edificio.
            // PAUTA: % de la tabla por fecha (intacto).
            const porcentajeComisionEdificio = isBTL
              ? comisionIntermediarioPorcentaje || 0
              : calcularPorcentajeComisionEdificio(edificioComision, facturafecha);

            // BTL no tiene intermediación → neto = bruto (f.importe). PAUTA: neto proporcional ya calculado.
            const importeNetoFila = isBTL ? importe : importeNeto;

            const participacion = denomComision > 0 ? pantallasEdificioAjustadas / denomComision : 0;
            const comisionCalculada = importeNetoFila * (porcentajeComisionEdificio / 100);

            // Regla de corte (deuda edificios): toda venta facturada antes del 01/01/2026 se muestra como
            // recaudada (pill verde "Recaudado", sin fecha), tenga o no cobro real. Mismo criterio que
            // getSaldoEdificio.js:268. Fecha por fila = facturafecha (PAUTA/BTL directo; contratos ya
            // llevan mesObj.facturafecha copiado en el explode).
            const esRecaudadoPreCorte = facturafecha && facturafecha < "2026-01-01";

            return (
              <tr className="table-striped" key={id}>
                <td className="text-center">{isBTL ? "BTL" : "PAUTA"}</td>
                <td className="text-center">{facturanumero}</td>
                <td className="text-center">
                  {`${new Date(facturafecha).getUTCDate().toString().padStart(2, "0")}/${(new Date(facturafecha).getUTCMonth() + 1).toString().padStart(2, "0")}/${new Date(facturafecha).getUTCFullYear().toString().slice(-2)}`}
                </td>
                <td className="text-center">{cliente}</td>
                <td className="text-center">{isBTL ? "-" : denomComision}</td>
                <td className="text-center">
                  {isBTL
                    ? "-"
                    : Number(participacion * 100).toLocaleString(getLocale(pais), {
                        minimumFractionDigits: decimales(pais),
                        maximumFractionDigits: decimales(pais),
                      }) + "%"}
                </td>
                {onDetalle && (
                  <td className="text-center">
                    {!isBTL && __rawSale ? (
                      <span
                        style={{ cursor: "pointer", color: "#0d6efd", textDecoration: "underline" }}
                        onClick={() => onDetalle(__rawSale)}
                      >
                        Detalle
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                )}
                <td className="text-end">
                  {`${moneda(pais)} ${(isBTL ? importe : participacion * importe).toLocaleString(getLocale(pais), { minimumFractionDigits: decimales(pais), maximumFractionDigits: decimales(pais) })}`}
                </td>
                <td className="text-center">{intermediario}</td>
                <td className="text-center">
                  {isBTL
                    ? "0%"
                    : comisionIntermediarioPorcentaje > 0
                      ? `${Number(comisionIntermediarioPorcentaje).toFixed(1)}%`
                      : "-"}
                </td>
                <td className="text-end">
                  {`${moneda(pais)} ${importeNetoFila.toLocaleString(getLocale(pais), { minimumFractionDigits: decimales(pais), maximumFractionDigits: decimales(pais) })}`}
                </td>
                <td className="text-center">
                  {isBTL ? `${Math.round(porcentajeComisionEdificio)}%` : `${porcentajeComisionEdificio}%`}
                </td>
                <td className="text-end">
                  {`${moneda(pais)} ${comisionCalculada.toLocaleString(getLocale(pais), { minimumFractionDigits: decimales(pais), maximumFractionDigits: decimales(pais) })}`}
                </td>
                <td
                  className="text-center"
                  style={{
                    backgroundColor: esRecaudadoPreCorte || facturafechacobro ? "#198754" : "#dc3545",
                    color: "white",
                  }}
                >
                  {esRecaudadoPreCorte
                    ? "Recaudado"
                    : facturafechacobro && !isNaN(new Date(facturafechacobro).getTime())
                      ? `${new Date(facturafechacobro).getUTCDate().toString().padStart(2, "0")}/${(new Date(facturafechacobro).getUTCMonth() + 1).toString().padStart(2, "0")}/${new Date(facturafechacobro).getUTCFullYear().toString().slice(-2)}`
                      : plazocobro && !isNaN(new Date(plazocobro).getTime())
                        ? `${new Date(plazocobro).getUTCDate().toString().padStart(2, "0")}/${(new Date(plazocobro).getUTCMonth() + 1).toString().padStart(2, "0")}/${new Date(plazocobro).getUTCFullYear().toString().slice(-2)}`
                        : "Pendiente"}
                </td>
              </tr>
            );
          })}
        </tbody>
        {mostrarFooter && (
          <tfoot>
            <tr className="table-dark">
              <td colSpan="6" className="text-start">
                TOTALES DEL PERÍODO SELECCIONADO
              </td>
              {onDetalle && <td />}
              <td className="text-end">
                {moneda(pais)}
                {(totales.totalImporte || 0).toLocaleString(getLocale(pais), {
                  maximumFractionDigits: decimales(pais),
                  minimumFractionDigits: decimales(pais),
                })}
              </td>
              <td className="text-center" />
              <td className="text-center">
                {totales.totalImporte
                  ? (100 - (totales.facturado / totales.totalImporte) * 100).toLocaleString(getLocale(pais), {
                      maximumFractionDigits: 1,
                      minimumFractionDigits: 1,
                    })
                  : "0"}
                %
              </td>
              <td className="text-end">
                {moneda(pais)}
                {(totales.facturado || 0).toLocaleString(getLocale(pais), {
                  maximumFractionDigits: decimales(pais),
                  minimumFractionDigits: decimales(pais),
                })}
              </td>
              <td className="text-center">
                {totales.facturado
                  ? ((totales.comision / totales.facturado) * 100).toLocaleString(getLocale(pais), {
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                    })
                  : "0"}
                %
              </td>
              <td className="text-end">
                {moneda(pais)}
                {(totales.comision || 0).toLocaleString(getLocale(pais), {
                  maximumFractionDigits: decimales(pais),
                  minimumFractionDigits: decimales(pais),
                })}
              </td>
              <td className="text-end" style={{ backgroundColor: "#198754", color: "white" }}>
                {moneda(pais)}
                {(totales.comisionCobrada || 0).toLocaleString(getLocale(pais), {
                  maximumFractionDigits: decimales(pais),
                  minimumFractionDigits: decimales(pais),
                })}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};
